import { useAppContext } from 'src/contexts/AppContext';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import {
  CommissionReportV2Request,
  CostOfGoodsSoldReportV2Request,
  InventoryReportV2Request,
  PurchaseReportV2Request,
  ReportV2Client,
  SaleLineItemReportV2Request,
  SalesReportV2Request,
  SaleTotalCostAdjustmentReportV2Request,
  SoldTicketReportV2Request,
  TicketGroupReportV2Request,
} from 'src/WebApiController';

import { ReportExportOptionsV2 } from './ReportsExport.types';
import { useReportsExportV2Inner } from './useReportsExportV2Inner';

type ReportsExportInnerProps = {
  reportExportOptions: ReportExportOptionsV2;
  onReportExported: () => void;
};

export function ReportsExportV2({
  reportExportOptions,
  onReportExported,
}: {
  reportExportOptions: ReportExportOptionsV2 | null;
  onReportExported: () => void;
}) {
  // Loading report metrics depend on the context provider (need to wait for the context provider to load the report metrics)

  // If reportToExport is null,
  // no reports need to be exported, so not rendering the <ReportsInventoryExportInner component

  // If reportToExport is not null, then render the <ReportsInventoryExportInner component.
  // It will load the report metrics and export the report to a PDF file
  // After the report is exported, the onReportExported callback will be called to reset the reportToExport state to null
  if (reportExportOptions == null) return null;

  if (
    reportExportOptions.reportToExport.reportType === ReportTypesV2.InventoryV2
  ) {
    return (
      <ReportsListingServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (
    reportExportOptions.reportToExport.reportType === ReportTypesV2.Purchase
  ) {
    return (
      <ReportsPurchaseServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (
    reportExportOptions.reportToExport.reportType === ReportTypesV2.SaleLineItem
  ) {
    return (
      <ReportsSaleLineItemServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (
    reportExportOptions.reportToExport.reportType === ReportTypesV2.SoldTicket
  ) {
    return (
      <ReportsSoldTicketServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (reportExportOptions.reportToExport.reportType === ReportTypesV2.SaleV2) {
    return (
      <ReportsSaleServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (
    reportExportOptions.reportToExport.reportType ===
    ReportTypesV2.CostOfGoodsSold
  ) {
    return (
      <ReportsCostOfGoodsSoldServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (
    reportExportOptions.reportToExport.reportType ===
    ReportTypesV2.SaleCostAdjustments
  ) {
    return (
      <ReportsSaleCostAdjustmentsServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (
    reportExportOptions.reportToExport.reportType === ReportTypesV2.Commission
  ) {
    return (
      <ReportsCommissionServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  if (
    reportExportOptions.reportToExport.reportType === ReportTypesV2.TicketGroup
  ) {
    return (
      <ReportsTicketGroupServerSideExportInner
        reportExportOptions={reportExportOptions}
        onReportExported={onReportExported}
      />
    );
  }

  return null;
}

function ReportsListingServerSideExportInner(props: ReportsExportInnerProps) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<InventoryReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadListingReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsPurchaseServerSideExportInner(props: ReportsExportInnerProps) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<PurchaseReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadPurchaseReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsSaleLineItemServerSideExportInner(
  props: ReportsExportInnerProps
) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<SaleLineItemReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadSaleLineItemReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsSoldTicketServerSideExportInner(
  props: ReportsExportInnerProps
) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<SoldTicketReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadSoldTicketReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsSaleServerSideExportInner(props: ReportsExportInnerProps) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<SalesReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadSaleReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsCostOfGoodsSoldServerSideExportInner(
  props: ReportsExportInnerProps
) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<CostOfGoodsSoldReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadCostOfGoodsSoldReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsSaleCostAdjustmentsServerSideExportInner(
  props: ReportsExportInnerProps
) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<SaleTotalCostAdjustmentReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadSaleTotalCostAdjustmentReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsCommissionServerSideExportInner(
  props: ReportsExportInnerProps
) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<CommissionReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadCommissionReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}

function ReportsTicketGroupServerSideExportInner(
  props: ReportsExportInnerProps
) {
  const { activeAccountWebClientConfig } = useAppContext();

  useReportsExportV2Inner<TicketGroupReportV2Request>({
    ...props,
    downloadReportMetrics: (
      req,
      reportName,
      fileType,
      supportBackgroundProcess
    ) =>
      new ReportV2Client(
        activeAccountWebClientConfig
      ).downloadTicketGroupReportMetrics(
        req,
        reportName,
        fileType,
        supportBackgroundProcess
      ),
  });

  return <></>;
}
